import { buttonStyle } from 'components/buttons/buttonStyle';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import { Button } from 'components/ui/button';
import { format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useState } from 'react';
import { PersonalRateDraft, PricelistTabDraft } from 'types/matters';
import { MatterFollowedPricelist } from 'types/pricelists';
import { t } from 'utils/getTranslations';
import { CasePeopleRates } from '../CasePeopleRates';
import { FollowedPricelistLine } from '../FollowedPricelistLine';
import { inForceListOn, tabLabel, undatedPeriodLabel } from './matterPricelistEditorUtils';
import { PersonalRatesLedger, RateCandidate } from './PersonalRatesLedger';
import { useToday } from './useEmployeePricing';

// The case's own prices for chosen people, which every period keeps; the rest of the list stays read-only.
interface ExceptionsEditor {
    rows: PersonalRateDraft[];
    activeEmployees: RateCandidate[] | undefined;
    caseTeamIds: Set<number>;
    isValidating: boolean;
    onChange: (rows: PersonalRateDraft[]) => void;
}

interface Props {
    followed: MatterFollowedPricelist;
    // The followed list's periods as the case's lists, the exceptions in them; read-only.
    tabs: PricelistTabDraft[];
    openedDate: string | null;
    currencyTypeCode?: string | null;
    // The case already follows this list: an own copy of it can be made here.
    onMakeOwnCopy?: () => void;
    // The follow was picked in this edit: it can be dropped for the prices the case had.
    onKeepOwnPrices?: () => void;
    exceptions?: ExceptionsEditor;
}

/**
 * The case editor's right pane while the case follows a list: its periods, read-only, the case's price exceptions
 * and the way out of it.
 */
export function CaseFollowPane({
    followed,
    tabs,
    openedDate,
    currencyTypeCode,
    onMakeOwnCopy,
    onKeepOwnPrices,
    exceptions,
}: Props): JSX.Element {
    const dateFormat = useEffectiveDateFormat();
    const today = useToday();
    const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
    const active = tabs.find((tab) => tab.key === activeKey) ?? inForceListOn(tabs, today) ?? tabs[0];
    const formatDate = (date: string) => format(parseISO(date), dateFormat);

    return (
        <div className="flex w-full flex-col gap-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <FollowedPricelistLine followed={followed} />
                <div className="flex gap-2">
                    {onMakeOwnCopy ? (
                        <Button {...buttonStyle({ variant: 'outlined', size: 'extraSmall' })} onClick={onMakeOwnCopy}>
                            {t('pricelists.case-follow.make-own-copy')}
                        </Button>
                    ) : null}
                    {onKeepOwnPrices ? (
                        <Button {...buttonStyle({ variant: 'text', size: 'extraSmall' })} onClick={onKeepOwnPrices}>
                            {t('pricelists.case-follow.keep-own-prices')}
                        </Button>
                    ) : null}
                </div>
            </div>
            {tabs.length > 1 ? (
                <LineTabs value={active?.key} onValueChange={(key) => setActiveKey(String(key))}>
                    <LineTabsList arrows={{ back: t('back'), next: t('next') }}>
                        {tabs.map((tab) => (
                            <LineTab key={tab.key} value={tab.key} size="compact">
                                {tab.isBase
                                    ? undatedPeriodLabel(tabs, formatDate)
                                    : tabLabel(
                                          tab,
                                          openedDate,
                                          formatDate,
                                          (date) => t('pricelists.case-editor.from-date', { date }),
                                          t('pricelists.employee.period-all-dates')
                                      )}
                            </LineTab>
                        ))}
                    </LineTabsList>
                </LineTabs>
            ) : null}
            <CasePeopleRates rows={active?.personalRates ?? []} currencyTypeCode={currencyTypeCode} />
            <p className="text-grey-500 text-[0.72rem] text-pretty">{t('pricelists.case-follow.follower-hint')}</p>
            {exceptions ? (
                <div className="flex flex-col gap-1 pt-2">
                    <hr className="mb-2 border-zinc-300/60" />
                    <PersonalRatesLedger
                        rows={exceptions.rows}
                        activeEmployees={exceptions.activeEmployees}
                        caseTeamIds={exceptions.caseTeamIds}
                        roleRates={[]}
                        currencyTypeCode={currencyTypeCode}
                        isValidating={exceptions.isValidating}
                        perEmployee
                        exceptions
                        onChange={exceptions.onChange}
                    />
                    <p className="text-grey-500 text-[0.72rem] text-pretty">
                        {t('pricelists.case-follow.exceptions-hint')}
                    </p>
                </div>
            ) : null}
        </div>
    );
}
